import React, { useEffect, useState, useContext, useMemo } from "react";
import AuthContext from "../../contexts/auth-context";
import useAxios from "../../hooks/useAxios";
import {
    Box,
    Typography,
    Pagination,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import { toastify_status, PAGE_SIZE, urls } from "../../components/config/variables";
import Toastify from "../../components/ui/Toastify";
import { ArrowDropUp, ArrowDropDown, ExpandMore, ExpandLess } from "@mui/icons-material";
import FilterModal from "../../components/filter/FilterModal";
import TuneIcon from '@mui/icons-material/Tune';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import { validateLabTestResults } from "../../Utils/validateHttpResponse";
import { useLocation } from "react-router-dom";
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { dateFormatter } from "../../Utils/DateFormatter";
import PreviewLabFileDialog from "../../components/ui/PreviewLabFileDialog/PreviewLabFileDialog";
import PreviewIcon from '@mui/icons-material/Preview';

const LabTestResults = () => {
    const { operation } = useAxios();
    const { token } = useContext(AuthContext);
    const location = useLocation()
    const dispensedToday = location.state?.dispensedToday;
    const [filterOpen, setFilterOpen] = useState(false);

    // get todays date yyyy-mm-dd
    const getToday = () => new Date().toISOString().split('T')[0];
    const [fromDate, setFromDate] = useState(dispensedToday ? getToday() : '');
    const [toDate, setTodate] = useState(dispensedToday ? getToday() : '');
    const [patientFilter, setPatientFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearch, setIsSearch] = useState(false);
    const [expanded, setExpanded] = useState([]); // ---> for expandable rows
    const [cptIdToPreview, setCptIdToPreview] = useState(undefined);
    // store the cpt ids with the same encounter
    const [cptIdsToPreview, setCptIdsToPreview] = useState([]); // ---> to store the rows with the same encounter for previewing in the dialog.

    // used to pass to the filters modal to update the filters after delete a spesific filter
    const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)

    const [dispensingRecords, setDispensingRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    // pagination
    const [currentPage, setCurrentPage] = useState(1);

    // Sorting
    const [sortBy, setSortBy] = useState("processed_at");
    const [sortDirection, setSortDirection] = useState("desc")

    // for expandable rows
    const handleExpandClick = (id) => {
        setExpanded((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleSort = (columnKey) => {
        if (sortBy === columnKey) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(columnKey);
            setSortDirection("asc");
        }
    };

    // Sort
    const filteredData = useMemo(() => {
        // Grouping by patient_name and encounter
        const grouped = {};
        dispensingRecords.forEach((item) => {
            const key = `${item.patient_name}__${item.encounter}`;
            if (!grouped[key]) {
                grouped[key] = {
                    id: key,
                    patient_name: item.patient_name,
                    encounter: item.encounter,
                    patient_records: [],
                };
            }
            grouped[key].patient_records.push({
                processed_by_name: item.processed_by_name,
                cpt_code: item.cpt_code,
                cpt_description: item.cpt_description,
                processed_at: item.processed_at,
                cpt_id: item.encounter_cpt,
                encounterId: item.encounter,
            });
        });

        let dataArray = Object.values(grouped);

        // Sorting
        dataArray.sort((a, b) => {
            let result = 0;
            if (sortBy === "patient_name") {
                result = a.patient_name.localeCompare(b.patient_name);
            } else {
                const aVal = a.patient_records[0]?.[sortBy] || "";
                const bVal = b.patient_records[0]?.[sortBy] || "";
                result = String(aVal).localeCompare(String(bVal));
            }
            return sortDirection === "asc" ? result : -result;
        });

        return dataArray;
    }, [dispensingRecords, sortBy, sortDirection]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const indexOfLastRow = currentPage * PAGE_SIZE;
    const indexOfFirstRow = indexOfLastRow - PAGE_SIZE;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (fromDate) {
            params.set('from_date', fromDate);
        }
        if (toDate) {
            params.set('to_date', toDate);
        }
        if (patientFilter) {
            params.set('patient_name', patientFilter);
        }

        if (isSearch && searchQuery) {
            params.set('cpt_code', searchQuery);
        }

        operation(
            {
                method: "GET",
                url: `${urls.LAB_TESTS_RESULTS}?${params}`,
                headers: { Authorization: `token ${token}` },
            },
            (res) => {
                if (validateLabTestResults(res)) {
                    setDispensingRecords(res || []);
                } else {
                    Toastify({ message: "Invalid response for lab test results.", state: toastify_status.ERROR });
                }
                setLoading(false);
            },
            () => {
                setLoading(false);
                Toastify("Failed to load lab test results.", toastify_status.ERROR);
            }
        );
        // eslint-disable-next-line
    }, [fromDate, toDate, patientFilter, isSearch]);

    const handleChangePage = (_, value) => {
        setCurrentPage(value);
    };

    const tableHeaders = [
        { label: "Patient Name", key: "patient_name", width: '25%' },
        { label: "Procedure", key: "cpt_description", width: '22%' },
        { label: "Code", key: "cpt_code", width: '10%' },
        { label: "Uploaded by", key: "processed_by_name", width: '20%' },
        { label: "Uploaded Date", key: "processed_at", width: '18%' },
        { label: "Preview", key: "preview", width: '5%' },

    ]

    const handleApplyFilters = (selected) => {
        setFromDate(selected.fromDate);
        setTodate(selected.toDate);
        setPatientFilter(selected.patient);
        setCurrentPage(1);
    };

    const deleteSpesificFilter = (filterKey) => {
        const updatedFilters = {
            fromDate: fromDate,
            toDate: toDate,
            patient: patientFilter,
            [filterKey]: ''
        };

        handleApplyFilters(updatedFilters);
        setNewFiltersAfterDelete(updatedFilters);
    };

    // Search Input Handlers
    const handleSearchClick = (event) => {
        if (searchQuery !== '') {
            event.preventDefault();
            setIsSearch(true);
        }
    };

    // Clear Search Input
    const handleClearSearch = () => {
        setIsSearch(false);
        setSearchQuery('');
    };

    useEffect(() => {
        if (isSearch && searchQuery === '') {
            handleClearSearch();
        }
        // eslint-disable-next-line
    }, [searchQuery, isSearch]);

    const handlePreview = (rec) => {
        console.log(rec)
      const foundPatient = currentRows.find((row) => row.encounter === rec.encounterId);
      setCptIdToPreview(rec.cpt_id);
      // store the cpt ids with the same encounter
      setCptIdsToPreview(currentRows.filter((row) => row.encounter === rec.encounterId)[0].patient_records.map((rec) => ({ cpt_id: rec.cpt_id, patient_name: foundPatient.patient_name  })));
    }

    const handleDeleteCptIdToPreview = () => {
        setCptIdToPreview(undefined);
    }

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}>
            <Box>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >


                    <Typography sx={{ fontWeight: "500", fontSize: "20px" }}>
                        Lab Test Results
                    </Typography>
                </Box>

                <Box sx={{ mb: 2, display: 'flex' }}>
                    {/* Search */}
                    <TextField
                        label="Search CPT Code"
                        sx={{ mr: 2, width: '300px' }}
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && handleSearchClick(event)}
                        InputProps={{
                            startAdornment: searchQuery && (
                                <InputAdornment position="start">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearchClick}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {/* Filters Button */}
                    <Box
                        onClick={() => setFilterOpen(true)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            mr: 2,
                            borderRadius: "5px",
                            bgcolor: "primary.main",
                            color: "white",
                            cursor: "pointer",
                            transition: ".2s",
                            "&:hover": { opacity: 0.9 },
                        }}
                    >
                        <TuneIcon fontSize="small" />
                        <Typography fontWeight={600}>All Filters</Typography>
                    </Box>
                    {/* Active Filters */}
                    {[
                        { label: "From Date", value: fromDate, key: "fromDate" },
                        { label: "To Date", value: toDate, key: "toDate" },
                        { label: "Patient", value: patientFilter, key: "patient" },
                    ]
                        .filter(f => f.value)
                        .map(f => (
                            <Box
                                key={f.key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    mr: 1,
                                    borderRadius: "10px",
                                    bgcolor: "background.default",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                }}
                            >
                                <Typography fontWeight={500}>
                                    {f.label}: {f.value}
                                </Typography>

                                <HighlightOffIcon
                                    onClick={() => deleteSpesificFilter(f.key)}
                                    sx={{
                                        cursor: "pointer",
                                        fontSize: 18,
                                        color: "text.secondary",
                                        "&:hover": { color: "error.main" },
                                    }}
                                />
                            </Box>
                        ))}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: '555px' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map((header) => (
                                        <TableCell
                                            key={header.key}
                                            onClick={() => header.key !== 'preview' && handleSort(header.key)}
                                            sx={{
                                                backgroundColor: '#F5F5F5', color: '#000000c7', fontWeight: '500', fontSize: '16px',
                                                border: '1px solid #d9d9d9', textAlign: 'center', cursor: 'pointer', width: header.width, py: 1.5
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography sx={{ fontWeight: "500", fontSize: "15px" }}>{header.label}</Typography>
                                                {sortBy === header.key && (sortDirection === 'asc' ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />)}
                                            </Box>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentRows.map((patient) => (
                                    <React.Fragment key={patient.id}>
                                        <TableRow
                                            onClick={() => handleExpandClick(patient.id)}
                                            sx={{ backgroundColor: '#FCFEFE', cursor: "pointer", "&:hover": { backgroundColor: "#f9f9f9" } }}
                                        >
                                            <TableCell sx={{ width: '25%', border: '1px solid #d9d9d9', textAlign: 'left', px: 2 }}>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <IconButton size="small">
                                                        {expanded.includes(patient.id) ? <ExpandLess /> : <ExpandMore />}
                                                    </IconButton>
                                                    <Typography sx={{ fontWeight: "500", fontSize: "15px" }}>
                                                        {patient.patient_name}
                                                    </Typography>
                                                    <Typography sx={{
                                                        ml: "auto", bgcolor: "#FF0000", color: "white", px: 1, py: 0.2,
                                                        borderRadius: "12px", fontSize: "11px", fontWeight: "bold"
                                                    }}>
                                                        {patient.patient_records.length}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ border: '1px solid #d9d9d9' }} colSpan={5} />
                                        </TableRow>

                                        {expanded.includes(patient.id) && patient.patient_records.map((rec, i) => (
                                            <TableRow key={i} sx={{ backgroundColor: "#fafafa" }}>
                                                <TableCell sx={{ border: '1px solid #d9d9d9' }} />
                                                <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                    <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                        {rec.cpt_description}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                    <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                        {rec.cpt_code}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                    <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                        {rec.processed_by_name}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                    <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                        {dateFormatter(rec.processed_at)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        border: '1px solid #d9d9d9',
                                                        textAlign: "center",
                                                        width: 120,
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                                                        {/* Preview */}
                                                        <Tooltip title='Preview file' arrow>
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePreview(rec);
                                                                }}
                                                                sx={{
                                                                    color: "#0BABE1",
                                                                    backgroundColor: "#E3F2FD",
                                                                    "&:hover": { backgroundColor: "#BBDEFB" }
                                                                }}
                                                            >
                                                                <PreviewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handleChangePage}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                )}

                {/* Filter Modal */}
                <FilterModal
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    onApply={handleApplyFilters}
                    newFiltersAfterDelete={newFiltersAfterDelete}
                    initialFilters={{
                        fromDate,
                        toDate,
                        patient: patientFilter,
                    }}
                    fields={["date", "patient"]}
                />
                <PreviewLabFileDialog cptIdToPreview={cptIdToPreview} cptIdsToPreview={cptIdsToPreview} handleDeleteCptIdToPreview={handleDeleteCptIdToPreview}/>
            </Box>
        </motion.div>

    );
};

export default LabTestResults;